/**
 * EventDetailsPage - Listagem Completa de Eventos
 * 
 * Página com listagem completa de eventos com filtros
 */

import { type FC, useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import StandardPage from '@/components/Common/StandardPage';
import DataTable from '@/components/DataTable/DataTable';
import { createEventColumns } from '@/components/DataTable/eventColumns';
import EventLogsModal from '@/components/EventLogsModal';
import { eventService } from '@/services';
import type { EventDetailsResponse, EventDetailsFilters, PageResponse } from '@/types';
import styles from './EventsPage.module.css';

export const EventDetailsPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<EventDetailsResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  // Event logs modal state
  const [selectedEvent, setSelectedEvent] = useState<EventDetailsResponse | null>(null);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const currentPage = Number(searchParams.get('page')) || 0;
  const pageSize = Number(searchParams.get('size')) || 20;

  useEffect(() => {
    loadEvents();
  }, [currentPage, pageSize]);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: EventDetailsFilters = {
        page: currentPage,
        size: pageSize,
        sort: 'createdAt,desc',
      };

      const response: PageResponse<EventDetailsResponse> = await eventService.getEventDetails(filters);
      
      setEvents(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      setError('Erro ao carregar eventos');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString(), size: pageSize.toString() });
  };

  const handleRefresh = () => {
    loadEvents();
  };

  /**
   * Handle view event logs
   */
  const handleViewLogs = useCallback((event: EventDetailsResponse) => {
    setSelectedEvent(event);
    setIsLogsModalOpen(true);
  }, []);

  /**
   * Handle close logs modal
   */
  const handleCloseLogsModal = useCallback(() => {
    setIsLogsModalOpen(false);
    setSelectedEvent(null);
  }, []);

  /**
   * Create columns with actions memoized
   */
  const columns = useMemo(
    () => createEventColumns({ onViewLogs: handleViewLogs }),
    [handleViewLogs]
  );

  return (
    <StandardPage
      header={{
        title: "Todos os Eventos",
        subtitle: `${totalElements.toLocaleString()} eventos no sistema`,
        actions: [{
          label: "Atualizar",
          onClick: handleRefresh,
        }],
      }}
      error={error ? new Error(error) : null}
    >
      <div className={styles.tableContainer}>
        <DataTable
          data={events}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="Nenhum evento encontrado"
          enablePagination={false}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              Anterior
            </button>
            <span>
              Página {currentPage + 1} de {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              Próxima
            </button>
          </div>
        )}
      </div>

      {/* Event Logs Modal */}
      <EventLogsModal
        open={isLogsModalOpen}
        onClose={handleCloseLogsModal}
        event={selectedEvent}
      />
    </StandardPage>
  );
};

export default EventDetailsPage;
